import React, { FC, useMemo, useState } from 'react'
import ReportSummaryModule from './ReportSummaryModule'
import Tips from '../common/Tips'
import Loading from '../common/Loading'
import { sortBy } from 'lodash'
import { secToHour, secToHourMinute } from '../../../utils/date'
import { setPath, setUrlId } from '../../../utils/url'
import { Link, useParams } from 'react-router-dom'
import { LangValue } from '../../../states/types/common'
import { URL } from '../../constants/url'
import InfoTips from '../common/InfoTips'
import { RootState } from '../../../states/containers/store'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DetailRow from './DetailRow'

interface Props {
  members: MemberDetail[] | undefined
}

interface MemberDetail {
  id: number
  name: string
  profile_image_url?: string | null
  label_groups: ListDetail[]
  labels: ListDetail[]
}

interface ListDetail {
  id: number
  name: string
  code?: string | null
  color: string
  time: number
  cost: number
}

interface LabelGroup extends ListDetail {
  labels: ListDetail[]
}

interface Label extends ListDetail {
  label_groups: ListDetail[]
}

interface FormattedList extends ListDetail {
  members: {
    id: number
    name: string
    profile_image_url?: string | null
    time: number
    cost: number
  }[]
}

const TeamSummaryModule: FC<Props> = (props) => {
  const {
    members,
  } = props

  const {
    login,
  } = useSelector((state: RootState) => state)

  const [isDisplayProjectWorkTime, setIsDisplayProjectWorkTime] = useState<boolean>(true)
  const [isDisplayLabelWorkTime, setIsDisplayLabelWorkTime] = useState<boolean>(true)

  const { language } = useParams<{ language: LangValue }>()

  const DISPLAY_LIMIT_NUM = 5

  const [isSelectProject, setIsSelectProject] = useState<boolean>(true)
  const [hoverName, setHoverName] = useState<string>("")
  const [hoverMemberId, setHoverMemberId] = useState<number>(0)

  const [isDisplayLabelGroupDetail, setIsDisplayLabelGroupDetail] = useState<boolean>(false)
  const [isDisplayLabelDetail, setIsDisplayLabelDetail] = useState<boolean>(false)

  const calcList = (list: ListDetail[]) => {
    let tmpList: { id: number; name: string; color: string; time: number; cost: number }[] = []
    list.forEach((v) => {
      if (!tmpList.map((w) => w.name).includes(v.name)) {
        tmpList.push({
          id: v.id,
          name: v.name,
          color: v.color,
          time: v.time,
          cost: v.cost,
        })
      } else {
        tmpList = tmpList.map((w) => {
          if (w.name === v.name) return { ...w, time: w.time + v.time }
          return w
        })
      }
    })
    return tmpList
  }

  const labelGroupList = useMemo(() => {
    if (!members) return []
    return calcList(members.map((v) => v.label_groups).flat())
  }, [members])

  const labelList = useMemo(() => {
    if (!members) return []
    return calcList(members.map((v) => v.labels).flat())
  }, [members])

  const legendList = useMemo(() => {
    if (!members) return []
    const selectList = isSelectProject ? labelGroupList : labelList
    return sortBy(selectList, "time").reverse().filter((v) => v.time > 0).map((v) => ({ name: v.name, color: v.color }))
  }, [members, isSelectProject])

  const maxTime = useMemo(() => {
    if (!members) return 0
    const tmpMax = Math.max(...members.map((v) => {
      if (isSelectProject) return v.label_groups.reduce((s, w) => s + w.time, 0)
      return v.labels.reduce((s, w) => s + w.time, 0)
    }))
    return (secToHour(tmpMax) + 1) * 3600
  }, [members, isSelectProject])

  const formattedLabelGroupList = useMemo(() => {
    if (!members) return []
    let tmpList: FormattedList[] = []
    members.map((member) => member.label_groups.forEach((v) => {
      const tmpMember = {
        id: member.id,
        name: member.name,
        profile_image: member.profile_image_url,
        time: v.time,
        cost: v.cost,
      }
      if (!tmpList.map((w) => w.name).includes(v.name)) {
        tmpList.push({
          ...v,
          members: [tmpMember],
        })
      } else {
        tmpList = tmpList.map((w) => {
          if (w.name === v.name) {
            return {
              ...w,
              time: w.time + v.time,
              cost: w.cost + v.cost,
              members: [...w.members, tmpMember],
            }
          }
          return w
        })
      }
    }))
    return tmpList
  }, [members])

  const formattedLabelList = useMemo(() => {
    if (!members) return []
    let tmpList: FormattedList[] = []
    members.map((member) => member.labels.forEach((v) => {
      const tmpMember = {
        id: member.id,
        name: member.name,
        profile_image: member.profile_image_url,
        time: v.time,
        cost: v.cost,
      }
      if (!tmpList.map((w) => w.name).includes(v.name)) {
        tmpList.push({
          ...v,
          members: [tmpMember],
        })
      } else {
        tmpList = tmpList.map((w) => {
          if (w.name === v.name) {
            return {
              ...w,
              time: w.time + v.time,
              cost: w.cost + v.cost,
              members: [...w.members, tmpMember],
            }
          }
          return w
        })
      }
    }))
    return tmpList
  }, [members])

  const buildDisplayGraphOrder = (list: ListDetail[]) => (
    legendList.map((v) => {
      const tmpDetails = list.filter((w) => w.name === v.name)
      if (tmpDetails.length) {
        return {
          ...tmpDetails[0],
          time: tmpDetails.reduce((s, w) => s + w.time, 0),
        }
      }
      return undefined
    }).filter((v):v is NonNullable<typeof v> => !!v)
  )

  const buildDisplayDetail = (memberDetail:MemberDetail) => (
    isSelectProject ? memberDetail.label_groups : memberDetail.labels
  )

  const onMouseOverGraph = (id: number, name: string) => {
    setHoverName(name)
    setHoverMemberId(id)
  }

  const onMouseLeaveGraph = () => {
    setHoverName("")
    setHoverMemberId(0)
  }

  const buildMemberReportUrl = (id: number) => setPath(setUrlId(URL.RISK_MEMBER_DETAIL.path, 'id', id), language)

  return (
    <>
      <div className="summary_title">
        工数管理情報
      </div>
      <InfoTips>
        <>
          <div>
            工数管理機能で仕分けされた時間を集計しています
          </div>
          <div>
            それぞれプロジェクト別・業務別・メンバー別の割合と時間を表示しています
          </div>
        </>
      </InfoTips>
      {
        members?.length ? (
          <>
            {
              maxTime > 3600 ? (
                <>
                  <div className="category_title_area">
                    <div
                      className="category_title"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      プロジェクト別仕分け実績情報
                      <div className="tools" style={{ justifyContent: "space-between" }}>
                        {
                          login.permissions?.task_cost_view && (
                            <>
                              {
                                isDisplayProjectWorkTime ? (
                                  <div />
                                ) : (
                                  <div className="cost_viewable_label">
                                    コスト表示権限を持つユーザーのみ表示されています
                                  </div>
                                )
                              }
                              <button
                                type="button"
                                className={`display_switch ${isDisplayProjectWorkTime ? "check" : ""}`}
                                onClick={() => setIsDisplayProjectWorkTime((cur) => !cur)}
                              >
                                <div>
                                  <FontAwesomeIcon icon={['far', 'clock']} />
                                  <span>
                                    実績時間
                                  </span>
                                </div>
                                <div>
                                  <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                  <span>
                                    実績コスト
                                  </span>
                                </div>
                              </button>
                            </>
                          )
                        }
                      </div>
                    </div>
                  </div>
                  <ReportSummaryModule
                    list={formattedLabelGroupList}
                    type="time"
                    graphTitle="プロジェクト別仕分け実績情報"
                    headerTitle={`プロジェクト(実績${isDisplayProjectWorkTime ? "時間" : "コスト"}が多い順)`}
                    chartSize={300}
                    valueType={isDisplayProjectWorkTime ? "time" : "cost"}
                  />
                  {
                    isDisplayLabelGroupDetail ? (
                      <>
                        {
                          formattedLabelGroupList.length ? (
                            <>
                              <div className="list_row_wrapper">
                                <div className={`list_row header_area${login.permissions?.task_cost_view ? " cost_view" : ""}`}>
                                  <div className="title_display_area">
                                    プロジェクト名
                                  </div>
                                  <div className="work_time">
                                    業務時間
                                  </div>
                                  {
                                    login.permissions?.task_cost_view && (
                                      <div className="work_cost">
                                        業務コスト
                                        <Tips
                                          description="コスト表示権限を持つユーザーのみ表示されています"
                                          className="fit"
                                          icon={false}
                                        >
                                          <>
                                            <FontAwesomeIcon icon={['far', 'question-circle']} />
                                          </>
                                        </Tips>
                                      </div>
                                    )
                                  }
                                </div>
                              </div>
                              <div>
                                {
                                  formattedLabelGroupList.map((labelGroup) => (
                                    <DetailRow
                                      key={`detail_row_${labelGroup.name}_${labelGroup.id}`}
                                      list={{
                                        ...labelGroup,
                                        details: labelGroup.members,
                                      }}
                                      type="label_group"
                                      costView={login.permissions?.task_cost_view || false}
                                      round
                                    />
                                  ))
                                }
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                marginBottom: "24px",
                                fontSize: "14px",
                                textAlign: "center",
                              }}
                            >
                              データがありません
                            </div>
                          )
                        }
                      </>
                    ) : (
                      <></>
                    )
                  }
                  <div
                    onClick={() => setIsDisplayLabelGroupDetail(!isDisplayLabelGroupDetail)}
                    role="none"
                    style={{
                      margin: "24px 0",
                      textAlign: "center",
                      backgroundColor: "#4572ba",
                      color: "#FFFFFF",
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                  >
                    {isDisplayLabelGroupDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                  </div>
                  <div className="category_title_area">
                    <div
                      className="category_title"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      業務別仕分け実績情報
                      <div className="tools" style={{ justifyContent: "space-between" }}>
                        {
                          login.permissions?.task_cost_view && (
                            <>
                              {
                                isDisplayLabelWorkTime ? (
                                  <div />
                                ) : (
                                  <div className="cost_viewable_label">
                                    コスト表示権限を持つユーザーのみ表示されています
                                  </div>
                                )
                              }
                              <button
                                type="button"
                                className={`display_switch ${isDisplayLabelWorkTime ? "check" : ""}`}
                                onClick={() => setIsDisplayLabelWorkTime((cur) => !cur)}
                              >
                                <div>
                                  <FontAwesomeIcon icon={['far', 'clock']} />
                                  <span>
                                    実績時間
                                  </span>
                                </div>
                                <div>
                                  <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                  <span>
                                    実績コスト
                                  </span>
                                </div>
                              </button>
                            </>
                          )
                        }
                      </div>
                    </div>
                  </div>
                  <ReportSummaryModule
                    list={labelList}
                    type="time"
                    graphTitle="業務別仕分け実績情報"
                    headerTitle={`業務(実績${isDisplayLabelWorkTime ? "時間" : "コスト"}が多い順)`}
                    chartSize={300}
                    valueType={isDisplayLabelWorkTime ? "time" : "cost"}
                  />
                  {
                    isDisplayLabelDetail ? (
                      <>
                        {
                          formattedLabelList.length ? (
                            <>
                              <div className="list_row_wrapper">
                                <div className={`list_row header_area${login.permissions?.task_cost_view ? " cost_view" : ""}`}>
                                  <div className="title_display_area">
                                    業務名
                                  </div>
                                  <div className="work_time">
                                    業務時間
                                  </div>
                                  {
                                    login.permissions?.task_cost_view && (
                                      <div className="work_cost">
                                        業務コスト
                                        <Tips
                                          description="コスト表示権限を持つユーザーのみ表示されています"
                                          className="fit"
                                          icon={false}
                                        >
                                          <>
                                            <FontAwesomeIcon icon={['far', 'question-circle']} />
                                          </>
                                        </Tips>
                                      </div>
                                    )
                                  }
                                </div>
                              </div>
                              <div>
                                {
                                  formattedLabelList.map((label) => (
                                    <DetailRow
                                      key={`detail_row__${label.name}_${label.id}`}
                                      list={{
                                        ...label,
                                        details: label.members,
                                      }}
                                      type="label"
                                      costView={login.permissions?.task_cost_view || false}
                                      round
                                    />
                                  ))
                                }
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                marginBottom: "24px",
                                fontSize: "14px",
                                textAlign: "center",
                              }}
                            >
                              データがありません
                            </div>
                          )
                        }
                      </>
                    ) : (
                      <></>
                    )
                  }
                  <div
                    onClick={() => setIsDisplayLabelDetail(!isDisplayLabelDetail)}
                    role="none"
                    style={{
                      margin: "24px 0",
                      textAlign: "center",
                      backgroundColor: "#4572ba",
                      color: "#FFFFFF",
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                  >
                    {isDisplayLabelDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                  </div>
                  <div className="category_title_area">
                    <div className="category_title">
                      メンバー別仕分け実績情報
                    </div>
                    <div className="switch_area">
                      <button
                        className={isSelectProject ? "select" : ""}
                        type="button"
                        onClick={() => setIsSelectProject(true)}
                      >
                        プロジェクト
                      </button>
                      <button
                        className={isSelectProject ? "" : "select"}
                        type="button"
                        onClick={() => setIsSelectProject(false)}
                      >
                        業務
                      </button>
                    </div>
                  </div>
                  <div className="list_area">
                    <div className="legend_area">
                      {
                        legendList.map((v) => (
                          <div
                            key={`legend_${v.name}_${v.color}_${isSelectProject}`}
                            className="legend"
                            onMouseOver={() => setHoverName(v.name)}
                            onFocus={() => { /* */ }}
                            onMouseLeave={() => setHoverName("")}
                            role="none"
                          >
                            <div
                              className="color_box"
                              style={{
                                backgroundColor: v.color,
                              }}
                            />
                            {v.name}
                          </div>
                        ))
                      }
                    </div>
                    {
                      members?.map((v) => (
                        <>
                          <div className="list_row">
                            <div className={`name_area ${(!hoverMemberId || hoverMemberId === v.id) ? "" : "transparent"}`}>
                              {
                                v.profile_image_url ? (
                                  <img alt="" src={v.profile_image_url} className="profile_icon" />
                                ) : (
                                  <img alt="" src={`/static/images/profile/${(v.id || 0) % 12}.jpeg`} className="profile_icon" />
                                )
                              }
                              <Link to={buildMemberReportUrl(v.id)}>{v.name}</Link>
                            </div>
                            <div className="graph_area">
                              <div
                                className="graph_wrap"
                                style={{
                                  width: `${(buildDisplayDetail(v).reduce((s, val) => s + val.time, 0) / maxTime) * 100}%`,
                                }}
                              >
                                {
                                  buildDisplayGraphOrder(buildDisplayDetail(v)).map((w) => (
                                    <React.Fragment key={`${w.id}_${w.name}_${w.time}`}>
                                      <div
                                        // eslint-disable-next-line max-len
                                        className={`graph_detail ${(!hoverName || (!hoverMemberId && hoverName === w.name) || (hoverMemberId === v.id && hoverName === w.name)) ? "" : "transparent"}`}
                                        style={{
                                          width: `${(w.time / buildDisplayDetail(v).reduce((s, val) => s + val.time, 0)) * 100}%`,
                                          background: `linear-gradient(to right, ${w.color} 0 calc(100% - 1px), #FFFFFF calc(100% - 1px) 100%)`,
                                        }}
                                        onMouseOver={() => onMouseOverGraph(v.id, w.name)}
                                        onFocus={() => { /* */ }}
                                        onMouseLeave={() => onMouseLeaveGraph()}
                                      >
                                        <Tips
                                          // eslint-disable-next-line max-len
                                          description={`${w.name}%br%${secToHourMinute(w.time)} (${Math.floor((w.time / buildDisplayDetail(v).reduce((s, val) => s + val.time, 0)) * 1000) / 10}%)`}
                                          className="inner_graph"
                                          icon={false}
                                        >
                                          <div
                                            className="graph_text"
                                            style={{
                                              color: w.color,
                                            }}
                                          >
                                            {
                                              (w.time / maxTime) * 100 > DISPLAY_LIMIT_NUM && (
                                                <>
                                                  <div
                                                    style={{
                                                      fontSize: "14px",
                                                    }}
                                                  >
                                                    {secToHourMinute(w.time)}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "10px",
                                                    }}
                                                  >
                                                    {`(${
                                                      Math.floor(
                                                        (w.time / buildDisplayDetail(v).reduce((s, val) => s + val.time, 0)) * 1000,
                                                      ) / 10
                                                    }%)`}
                                                  </div>
                                                </>
                                              )
                                            }
                                          </div>
                                        </Tips>
                                      </div>
                                    </React.Fragment>
                                  ))
                                }
                              </div>
                            </div>
                          </div>
                        </>
                      ))
                    }
                    <div className="ruler_area">
                      <div
                        className="scale_area"
                        style={{
                          left: 0,
                        }}
                      >
                        <div className="scale" />
                        {secToHourMinute(0)}
                      </div>
                      {
                        Array(5).fill(null).map((_v, i) => (
                          <div
                            className="scale_area"
                            style={{
                              left: `${(100 / 5) * (i + 1)}%`,
                            }}
                          >
                            <div className="scale" />
                            {secToHourMinute((maxTime / 5) * (i + 1))}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div
                      style={{
                        marginBottom: "24px",
                        padding: "0 24px",
                        fontSize: "14px",
                      }}
                    >
                      <p>
                        工数管理機能を使って業務仕分けをおこなうと、より詳細な分析情報が表示できます。
                      </p>
                      <p>
                        工数管理機能の使い方は
                        <a href="https://qasee.zendesk.com/hc/ja/articles/43881715166617" target="_blank" rel="noreferrer">
                          こちら
                        </a>
                        をご覧ください。
                      </p>
                      <p>
                        是非、ご活用ください。
                      </p>
                    </div>
                    <img
                      src="/static/images/task/image_8.png"
                      alt="工数管理を利用すれば、さらにわかる"
                      style={{
                        width: "70%",
                        margin: "0 15%",
                      }}
                    />
                  </div>
                </>
              )
            }
          </>
        ) : (
          <>
            <Loading />
          </>
        )
      }
    </>
  )
}

export default TeamSummaryModule
